import { getPreviousDays, getPreviousMonthsDateRanges, getPreviousWeeks } from '../../utils/getPrevWeeks';
import { formatDateYMD, todayYYYYMMDD } from '../../utils/formatDate';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import dayjs from 'dayjs';
import { useFetchHolidays } from '../../hooks/useFetchHolidays';

const WeekPickerStyle = styled.div`
  padding: 0 1rem;
  width: 100%;
  .oneWeekCalendar {
    overflow: auto;
    ::-webkit-scrollbar {
      height: 0rem;
    }
    gap: 0;
    justify-content: space-between;
    .dayWrapper {
      // flex-grow: 1;
      padding: 0.75rem;
      min-width: fit-content;
      // max-width: 2.75rem;
      border-radius: 2rem;
      text-align: center;
      cursor: pointer;
      .day {
        font-weight: var(--font-medium);
        color: var(--text-tertiary);
      }
      .date {
        color: var(--text-primary);
      }

      &.thinPadding {
        padding: 0.75rem 0.45rem;
      }

      &.sunday:not(.todayHighlight),
      &.holiday:not(.todayHighlight) {
        .day,
        .date {
          color: var(--red);
        }
      }

      &.saturday:not(.todayHighlight) {
        .day,
        .date {
          color: var(--blue);
        }
      }
    }
    .dayWrapper:hover {
      background-color: var(--tonal);
    }
    .todayHighlight,
    .todayHighlight:hover {
      background-color: var(--selected-primary);
      > div.day,
      > div.date {
        color: #fff;
      }
      > div.day {
        font-weight: 500;
      }
    }
  }
`;

interface IPicker {
  selectedValue: string;
  handleSelect: (value: string) => void;
  dayCount?: number;
}

interface IDayPicker extends IPicker {
  direction?: 'past' | 'future'; // 과거 또는 미래 날짜 표시
  baseDate?: string; // future 구간 시작일. 없으면 today
}

/**
 * 수정자 : 홍선영
 * 수정일자 : 2026-08-10
 * 수정내용 : DayPicker에 baseDate prop 추가 — direction='future'일 때 구간 시작일 지정 가능
 */
export const DayPicker = ({ dayCount = 14, selectedValue, handleSelect, direction = 'past', baseDate }: IDayPicker) => {
  const { t } = useTranslation();
  const today = todayYYYYMMDD();
  const { holidays, isLoading: holidaysLoading, isError: holidaysError } = useFetchHolidays(dayjs(selectedValue).format('YYYY'));

  // 미래 날짜 생성 함수
  const getFutureDays = (startDate: string, period: number) => {
    const start = dayjs(startDate);
    const days = [];

    for (let i = 0; i < period; i += 1) {
      const dayDate = start.add(i, 'day');
      days.push({
        dayName: dayDate.format('dddd').charAt(0),
        date: dayDate.format('DD'),
        fullDate: dayDate.format('YYYY-MM-DD'),
        isToday: dayDate.isSame(dayjs(), 'day'),
      });
    }

    return days;
  };

  const rangeStart = baseDate ? baseDate.replaceAll('-', '') : today;
  const twoWeekDays = direction === 'future' ? getFutureDays(rangeStart, dayCount) : getPreviousDays(today, dayCount);

  return (
    <WeekPickerStyle>
      <div className='oneWeekCalendar flex-between'>
        {twoWeekDays.map((el: any) => {
          const isHoliday = !holidaysLoading && !holidaysError && holidays.length > 0 && holidays?.some((holiday) => dayjs(holiday.sDate).format('MMDD') === dayjs(el.fullDate).format('MMDD'));
          const dayTypeClass = isHoliday ? 'holiday' : el.dayName === '일' ? 'sunday' : el.dayName === '토' ? 'saturday' : '';

          return (
            <div
              key={el.date}
              className={`${el.fullDate === formatDateYMD(selectedValue) ? 'todayHighlight' : ''} ${dayTypeClass} dayWrapper`}
              role='button'
              tabIndex={0}
              onClick={() => handleSelect(el.fullDate)}
            >
              <div className='day'>{t(el.dayName)}</div>
              <div className='date bold'>{el.date}</div>
            </div>
          );
        })}
      </div>
    </WeekPickerStyle>
  );
};

export const WeekPicker = ({ dayCount = 14, selectedValue, handleSelect }: IPicker) => {
  const { t } = useTranslation();
  const eightWeeks = getPreviousWeeks(8);

  return (
    <WeekPickerStyle>
      <div className='oneWeekCalendar flex-between'>
        {eightWeeks.map((el: any) => (
          <div key={el.weekNumber} className={`${el.weekNumber === selectedValue ? 'todayHighlight' : ''} dayWrapper`} role='button' tabIndex={0} onClick={() => handleSelect(el)}>
            <div className='day'>{el.month}</div>
            <div className='date bold'>
              {el.weekInMonth}
              {t('주')}
            </div>
          </div>
        ))}
      </div>
    </WeekPickerStyle>
  );
};

export const MonthPicker = ({ dayCount = 14, selectedValue, handleSelect }: IPicker) => {
  const { t } = useTranslation();
  const twelveMonths = getPreviousMonthsDateRanges(14);

  return (
    <WeekPickerStyle>
      <div className='oneWeekCalendar flex-between'>
        {twelveMonths
          .filter((_, index) => index > 1)
          .map((el: any) => (
            <div key={`${el.year}_${el.month}`} className={`${el.month === selectedValue ? 'todayHighlight' : ''} dayWrapper thinPadding`} role='button' tabIndex={0} onClick={() => handleSelect(el)}>
              <div className='day'>
                {el.year}
                {t('년')}
              </div>
              <div className='date bold'>
                {el.month}
                {t('월')}
              </div>
            </div>
          ))}
      </div>
    </WeekPickerStyle>
  );
};
